import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Box, IconButton, InputAdornment, styled } from "@mui/material";
import Button from "@mui/material/Button";
import styles from "./login.module.scss";
import { object, string, ValidationError } from "yup";
import constants from "../../config/constants.json";
import { apiRequest, catchErrorMessageHandler } from "../../infrastructure/backendService";
import {
  ContactNumberInput,
  CustomLeftIcon,
  CustomRequiredInputLabel,
} from "../common/StyledComponents/StyledFields";
import toast, { Toaster } from "react-hot-toast";
import ToastSuccess from "../common/toast/toastSuccess";
import ToastError from "../common/toast/toastError";
import { isNil } from "lodash";
import apiPath from "../../config/apiPaths.json";
import SuccessPopup from "../devotee/donation/successPopup";
import ErrorPopup from "../common/ErrorPopup";

const CustomTextField = styled(TextField)`
  & input {
    padding: 8px;
    height: 25px;
    border-radius: 8px;
    background: #fff9e4;
  }
  & fieldset {
    border-style: none;
  }
`;

interface UserForm {
  firstName: string;
  lastName: string;
  zipCode: string;
  contactNo: string;
}

interface validationFieldErrorsType {
  firstName: string;
  lastName: string;
  zipCode: string;
  contactNo: string;
}

interface forgotUserNameProps {
  setShowLoginForm: React.Dispatch<React.SetStateAction<boolean>>;
  setShowForgotUsername: React.Dispatch<React.SetStateAction<boolean>>;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ForgotUsername(props: forgotUserNameProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showReenterPassword, setReenterpassword] = useState(false);
  const [userForm, setUserForm] = useState<UserForm>({
    firstName: "",
    lastName: "",
    zipCode: "",
    contactNo: "",
  });

  const [userFormErrorMessage, setValidationFieldErrors] =
    useState<validationFieldErrorsType>({
      firstName: "",
      lastName: "",
      zipCode: "",
      contactNo: "",
    });
  const handleClickShowphonenumber = () => setReenterpassword((show) => !show);

  const handleMouseDownshowphonenumber = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const validationSchema = object().shape({
    firstName: string().required("First Name is required"),
    lastName: string().required("Last Name is required"),
    zipCode: string().required("Zipcode is required"),
  });

  const handleFieldChange = (field: any, value: any): void => {
    setUserForm({
      ...userForm,
      [field]: value,
    });
  };

  const onClickValidateCode = async (): Promise<void> => {
    const errors: validationFieldErrorsType = {
      firstName: "",
      lastName: "",
      zipCode: "",
      contactNo: "",
    };

    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(userForm, { abortEarly: false });
      // If validation passes, you can submit the form
      setValidationFieldErrors(errors);
      getUsernameApiCall();
    } catch (error) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            errors[e.path as keyof validationFieldErrorsType] = e.message;
          }
        });
      }
      setValidationFieldErrors(errors);
    }
  };

  const getUsernameApiCall = () => {
    const reqBody = {
      firstname: userForm.firstName,
      lastname: userForm.lastName,
      zipcode: userForm.zipCode,
      phonework: userForm.contactNo,
    };

    apiRequest({
      method: "POST",
      endpoint: apiPath.forgotLoginInformationApi,
      body: JSON.stringify(reqBody),
    })
      .then((res) => {
        console.log(res.data);
        const successMessage =
          "Sign-In information has been sent to your registered email.";
        if (res.data.Flag === true) {
          // toast.custom(<ToastSuccess message={res.data.Message} sent={""} />, {
          //   duration: 10000,
          // });
          toast.custom(<SuccessPopup headerMessage="Forgot Username" showMessage={res.data.Message} isOpen={isOpen} setIsOpen={setIsOpen} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          });
        } else {
          toast.custom(<ErrorPopup showMessage={res.data.Message} isOpen={isOpen} setIsOpen={setIsOpen} />, {
            duration: constants.TOAST_POPUP_DURATION,
          });
        }
        setUserForm({
          firstName: "",
          lastName: "",
          zipCode: "",
          contactNo: "",
        });
      })
      .catch((err) => {
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        props.setShowLoginForm(false);
        props.setShowForgotUsername(true);
      });
  };

  const onClickCancel = (): void => {
    // props.setShowLoginForm(true);
    // props.setShowForgotUsername(false);
    setUserForm({
      firstName: "",
      lastName: "",
      zipCode: "",
      contactNo: "",
    });
  };

  const navigateBack = () => {
    props.setShowLoginForm(true);
    props.setShowForgotUsername(false);
  };

  const getSignInFields = (): React.ReactElement => {
    return (
      <>
        <Toaster />
        <form onSubmit={onClickValidateCode} className={styles.Form}>
         <Box sx={{display:"flex",alignItems:"center"}}>
         <div onClick={navigateBack} style={{ marginRight: "10px" }}>
            <CustomLeftIcon />
          </div>
          <Typography align="left" sx={{ fontSize: "24px" }}>
            Forgot Username
          </Typography>
         </Box>
          <Typography sx={{fontSize:"14px"}}>
            If you do not remember your Sign-in Name, enter your First Name,
            Last Name, Zip Code and click on 'Get my Sign-in Name'.
          </Typography>
          <Grid xs={12} item sx={{marginTop:"8px"}}>
            {CustomRequiredInputLabel("First Name", true)}
            <CustomTextField
              fullWidth={true}
              value={userForm.firstName}
              onChange={(e) => {
                // Ensure only alphabetic characters are entered
                const enteredValue = e.target.value.replace(/[^a-zA-Z ]/g, '').slice(0,50); // Replace any non-alphabetic character with an empty string
                setValidationFieldErrors({
                  ...userFormErrorMessage,
                  firstName: "",
                });
                handleFieldChange("firstName", enteredValue);
              }}
              className={
                userFormErrorMessage.firstName
                  ? styles.errorTextField
                  : styles.textField
              }
            />

            {userFormErrorMessage.firstName && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {userFormErrorMessage.firstName}
              </Typography>
            )}
          </Grid>
          <Grid xs={12} item >
            {CustomRequiredInputLabel("Last Name", true)}
            <CustomTextField
              fullWidth={true}
              value={userForm.lastName}
              onChange={(e) => {
                // Ensure only alphabetic characters are entered
                const enteredValue = e.target.value.replace(/[^a-zA-Z ]/g, '').slice(0,50); // Replace any non-alphabetic character with an empty string
                setValidationFieldErrors({
                  ...userFormErrorMessage,
                  lastName: "",
                });
                handleFieldChange("lastName", enteredValue);
              }}
              className={
                userFormErrorMessage.lastName
                  ? styles.errorTextField
                  : styles.textField
              }
            />
            {userFormErrorMessage.lastName && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {userFormErrorMessage.lastName}
              </Typography>
            )}
          </Grid>
          <Grid xs={12} item >
            {CustomRequiredInputLabel("Zip Code", true)}
            <CustomTextField
              fullWidth={true}
              value={userForm.zipCode}
              onChange={(e) => {
                // Ensure only numbers are entered and limit to 10 characters
                let enteredValue = e.target.value.replace(/\D/g, ''); // Replace any non-digit character with an empty string
                enteredValue = enteredValue.slice(0, 10); // Limit to 10 characters
                setValidationFieldErrors({
                  ...userFormErrorMessage,
                  zipCode: "",
                });
                handleFieldChange("zipCode", enteredValue);
              }}
              className={
                userFormErrorMessage.zipCode
                  ? styles.errorTextField
                  : styles.textField
              }
            />
            {userFormErrorMessage.zipCode && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {userFormErrorMessage.zipCode}
              </Typography>
            )}
          </Grid>
          <Grid xs={12} item >
            {CustomRequiredInputLabel("Home Phone Number", false)}
            <CustomTextField
              type="tel"
              fullWidth
              required
              value={userForm.contactNo}
              onChange={(e) => {
                setValidationFieldErrors({
                  ...userFormErrorMessage,
                  contactNo: "",
                });
                const inputNumber = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                const slicedNumber = inputNumber.slice(0, 10); // Limit to 10 digits
                handleFieldChange("contactNo",slicedNumber);
              }}
              className={
                userFormErrorMessage.contactNo
                  ? styles.errorTextField
                  : styles.textField
              }
            />
          </Grid>
          <Grid xs={12} item sx={{ marginTop: "10px" }}>
            <Button
              color="warning"
              type="submit"
              fullWidth={true}
              variant="contained"
              onClick={onClickValidateCode}
            >
              Get Sign In Name
            </Button>
            <Button
            type="submit"
              color="warning"
              fullWidth={true}
              variant="outlined"
              sx={{ marginTop: "10px" }}
              onClick={onClickCancel}
            >
              Reset
            </Button>
          </Grid>
        </form>
      </>
    );
  };

  return <div>{getSignInFields()}</div>;
}
